/* Copyright (c) 2020-present CloudIO Inc */

import {
  fieldVariant,
  messageType,
} from 'componentregistry/registry/constants';
import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './Alert';
import { helpRenders } from './Alert_Help';

const AlertManager: ComponentManagerFn<'Alert'> = (platform: Platform) => {
  return {
    ...baseComponentManager<'Alert'>(platform, 'Alert'),
    type: 'Alert',
    name: 'Alert',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick'] as ItemType[]).includes(childType),
    defaultChildren: [],
    defaultValue,
    props: {
      severity: {
        label: 'Severity',
        type: 'select',
        options: messageType,
      },
      variant: {
        label: 'Variant',
        type: 'select',
        options: fieldVariant,
      },
      alertTitle: {
        label: 'Title',
        type: 'expression',
        returnType: async () => 'string',
      },
      alertText: {
        label: 'Message',
        type: 'expression',
        returnType: async () => 'string',
        required: true,
      },
      actionText: {
        label: 'Action Label',
        type: 'string',
      },
    },
    getDisplayText: (item) => item.props.severity,
    canHaveChildren: () => false,
    category: 'Dashboard',
    icon: { iconName: 'exclamation-triangle' },
    description: `**Overview**<br/>
The Alert component is a dedicated feedback element used to display short, critical messages to the user. Unlike a Popup, which interrupts the workflow and demands immediate action, an Alert appears inline with the content. It provides necessary context—such as success confirmations, system warnings, or error notifications—in a visually prominent way without blocking the user's current task.

**1. Communicating Urgency & Tone**<br/>
The most important configuration for an Alert is defining its purpose. By setting the severity, the component automatically adjusts its color and iconography to match standard user expectations.
* **Severity:** Selects the semantic meaning of the alert.
    * **Success:** (Green) Indicates a completed task, such as "Data Saved."
    * **Info:** (Blue) Neutral information, such as "System Update Available."
    * **Warning:** (Orange/Yellow) Cautionary advice, such as "Unsaved Changes."
    * **Error:** (Red) Critical failures, such as "Connection Lost."

**2. Content & Styling**<br/>
You can structure the text within the alert to ensure readability and choose a visual style that fits the density of your page.
* **Message:** The primary text body of the alert. This field is Required and supports expressions, allowing you to display dynamic error messages returned from an API.
* **Title:** An optional bold heading displayed above the message. Use this to provide a quick summary (e.g., Title: "Upload Failed", Message: "File size exceeds 5MB").
* **Variant:** Controls the visual weight of the component. (Standard: Light background; Filled: Solid high-contrast; Outlined: Transparent with border).

**3. Actions & Interactivity**<br/>
While Alerts are primarily informational, they can also serve as a prompt for quick actions (like "Undo" or "Retry").
* **Action Label:** Defines the text for an interactive button placed on the right side of the alert (e.g., "Retry").
* **Trigger Support:** To make the Action Label functional, you can add a TriggerOnClick logic component to the Alert. This allows the user to click the action text to execute a workflow, such as reloading a data store or navigating to a settings page.`,
    helpRenders,
  };
};

export default AlertManager;
